import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const FAQ = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  const faqs = [
    {
      category: t('faq.categories.general'),
      questions: [
        { q: t('faq.questions.whatIs.q'), a: t('faq.questions.whatIs.a') },
        { q: t('faq.questions.howLong.q'), a: t('faq.questions.howLong.a') },
        { q: t('faq.questions.roomTypes.q'), a: t('faq.questions.roomTypes.a') },
      ]
    },
    {
      category: t('faq.categories.pricing'),
      questions: [
        { q: t('faq.questions.creditSystem.q'), a: t('faq.questions.creditSystem.a') },
        { q: t('faq.questions.refund.q'), a: t('faq.questions.refund.a') },
        { q: t('faq.questions.creditsExpire.q'), a: t('faq.questions.creditsExpire.a') },
      ]
    },
    {
      category: t('faq.categories.usage'),
      questions: [
        { q: t('faq.questions.imageQuality.q'), a: t('faq.questions.imageQuality.a') },
        { q: t('faq.questions.preserveColors.q'), a: t('faq.questions.preserveColors.a') },
        { q: t('faq.questions.chooseStyle.q'), a: t('faq.questions.chooseStyle.a') },
        { q: t('faq.questions.imageStorage.q'), a: t('faq.questions.imageStorage.a') },
      ]
    },
    {
      category: t('faq.categories.technical'),
      questions: [
        { q: t('faq.questions.fileFormats.q'), a: t('faq.questions.fileFormats.a') },
        { q: t('faq.questions.mobile.q'), a: t('faq.questions.mobile.a') },
        { q: t('faq.questions.failedStyling.q'), a: t('faq.questions.failedStyling.a') },
      ]
    },
    {
      category: t('faq.categories.business'),
      questions: [
        { q: t('faq.questions.businessPlans.q'), a: t('faq.questions.businessPlans.a') },
        { q: t('faq.questions.commercial.q'), a: t('faq.questions.commercial.a') },
        { q: t('faq.questions.api.q'), a: t('faq.questions.api.a') },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      
      <Header />

      {/* Hero Section */}
      <section className="border-b border-border bg-muted/20">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-start gap-4">
            <HelpCircle className="h-12 w-12 text-primary flex-shrink-0" />
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {t('faq.title')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl">
                {t('faq.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <main className="flex-1">
        <section className="container mx-auto px-4 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((category, idx) => (
              <Card key={idx} className="p-6">
                <h2 className="text-2xl font-bold text-foreground mb-6">{category.category}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((faq, qIdx) => (
                    <AccordionItem key={qIdx} value={`item-${idx}-${qIdx}`}>
                      <AccordionTrigger className="text-left">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="bg-muted/20 py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('faq.moreQuestions')}
            </h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t('faq.moreQuestionsDesc')}
            </p>
            <Button size="lg" asChild>
              <a href="mailto:hej@estylingstudio.se">{t('faq.contactSupport')}</a>
            </Button>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary text-primary-foreground py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('faq.readyToTry')}
            </h2>
            <p className="text-lg mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              {t('faq.readyToTryDesc')}
            </p>
            <Link to={buildPath('/auth')}>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                {t('faq.getStartedFree')}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
